import { h, ref, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'

function normalizarVNodes (nodes) {
  return (Array.isArray(nodes) ? nodes : [nodes])
    .flat(Infinity)
    .filter(node => node !== null && node !== undefined && typeof node !== 'boolean')
}

function getOppositePlacement (placement) {
  const oppositePlacement = {
    top: 'bottom',
    bottom: 'top',
    left: 'right',
    right: 'left'
  }

  return oppositePlacement[placement] || 'top'
}

function calculatePosition (rect, tooltipRect, placement, gap) {
  switch (placement) {
    case 'bottom':
      return {
        top: rect.bottom + gap,
        left: rect.left + (rect.width - tooltipRect.width) / 2
      }
    case 'left':
      return {
        top: rect.top + (rect.height - tooltipRect.height) / 2,
        left: rect.left - tooltipRect.width - gap
      }
    case 'right':
      return {
        top: rect.top + (rect.height - tooltipRect.height) / 2,
        left: rect.right + gap
      }
    case 'top':
    default:
      return {
        top: rect.top - tooltipRect.height - gap,
        left: rect.left + (rect.width - tooltipRect.width) / 2
      }
  }
}

function overflowsViewport (position, tooltipRect, padding) {
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight

  return (
    position.left < padding ||
    position.left + tooltipRect.width > viewportWidth - padding ||
    position.top < padding ||
    position.top + tooltipRect.height > viewportHeight - padding
  )
}

function clampPosition (position, tooltipRect, padding) {
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight

  return {
    top: Math.min(
      Math.max(position.top, padding),
      Math.max(padding, viewportHeight - tooltipRect.height - padding)
    ),
    left: Math.min(
      Math.max(position.left, padding),
      Math.max(padding, viewportWidth - tooltipRect.width - padding)
    )
  }
}

function positionTooltip (triggerEl, tooltipEl, preferredPlacement) {
  if (!triggerEl || !tooltipEl) return preferredPlacement

  const rect = triggerEl.getBoundingClientRect()

  tooltipEl.style.top = '0px'
  tooltipEl.style.left = '0px'
  const tooltipRect = tooltipEl.getBoundingClientRect()

  const gap = 6
  const padding = 8

  let finalPlacement = preferredPlacement
  let position = calculatePosition(rect, tooltipRect, finalPlacement, gap)

  if (overflowsViewport(position, tooltipRect, padding)) {
    const fallbackPlacement = getOppositePlacement(preferredPlacement)
    const fallbackPosition = calculatePosition(rect, tooltipRect, fallbackPlacement, gap)

    if (!overflowsViewport(fallbackPosition, tooltipRect, padding)) {
      finalPlacement = fallbackPlacement
      position = fallbackPosition
    } else {
      finalPlacement = fallbackPlacement
      position = fallbackPosition
    }
  }

  const clampedPosition = clampPosition(position, tooltipRect, padding)

  tooltipEl.className = `tooltip ${finalPlacement} in`
  tooltipEl.style.top = clampedPosition.top + 'px'
  tooltipEl.style.left = clampedPosition.left + 'px'

  return finalPlacement
}

export default {
  name: 'PttTooltip',
  props: {
    text: { type: String, default: '' },
    trigger: { type: String, default: 'hover' },
    placement: { type: String, default: 'top' },
    modelValue: { type: Boolean, default: false }
  },
  emits: ['update:modelValue'],
  setup (props, { emit }) {
    const visible = ref(false)
    const tooltipRef = ref(null)
    const triggerRef = ref(null)
    let animationFrameId = null

    function setTooltipRef (el) {
      tooltipRef.value = el
    }

    function setTriggerRef (el) {
      triggerRef.value = el
    }

    function repositionTooltip () {
      if (!visible.value) return

      nextTick(() => {
        const triggerEl = triggerRef.value
        if (tooltipRef.value && triggerEl) {
          positionTooltip(triggerEl, tooltipRef.value, props.placement)
        }
      })
    }

    function stopTrackingPosition () {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId)
        animationFrameId = null
      }
    }

    function trackPosition () {
      stopTrackingPosition()

      const loop = () => {
        if (!visible.value) {
          animationFrameId = null
          return
        }

        repositionTooltip()
        animationFrameId = requestAnimationFrame(loop)
      }

      animationFrameId = requestAnimationFrame(loop)
    }

    function showTooltip () {
      visible.value = true
      emit('update:modelValue', true)
      repositionTooltip()
      trackPosition()
    }

    function hideTooltip () {
      stopTrackingPosition()
      visible.value = false
      emit('update:modelValue', false)
    }

    watch(() => props.modelValue, (val) => {
      if (props.trigger === 'manual') {
        visible.value = val
        if (val) {
          repositionTooltip()
          trackPosition()
        } else {
          stopTrackingPosition()
        }
      }
    })

    watch(() => props.placement, () => {
      repositionTooltip()
    })

    onMounted(() => {
      window.addEventListener('resize', repositionTooltip)
      window.addEventListener('scroll', repositionTooltip, true)
    })

    onBeforeUnmount(() => {
      window.removeEventListener('resize', repositionTooltip)
      window.removeEventListener('scroll', repositionTooltip, true)
      stopTrackingPosition()
      visible.value = false
    })

    return { visible, tooltipRef, triggerRef, setTooltipRef, setTriggerRef, showTooltip, hideTooltip }
  },
  render () {
    const rawDefault = this.$slots.default
    const defaultSlot = normalizarVNodes(typeof rawDefault === 'function' ? rawDefault() : (rawDefault || []))

    const events = {}
    if (this.trigger === 'hover') {
      events.onMouseenter = this.showTooltip
      events.onMouseleave = this.hideTooltip
    } else if (this.trigger === 'focus') {
      events.onFocus = this.showTooltip
      events.onBlur = this.hideTooltip
    }

    const children = [
      h('span', { ref: this.setTriggerRef, ...events, style: { display: 'inline-block' } }, defaultSlot)
    ]

    if (this.visible && this.text) {
      children.push(
        h('div', {
          style: {
            position: 'fixed',
            top: '0',
            left: '0',
            width: '0',
            height: '0',
            overflow: 'visible',
            pointerEvents: 'none',
            zIndex: '2147483647',
            isolation: 'isolate'
          }
        }, [
          h('div', {
            ref: this.setTooltipRef,
            class: `tooltip ${this.placement} in`,
            role: 'tooltip',
            style: {
              position: 'fixed',
              zIndex: '2147483647',
              display: 'block',
              isolation: 'isolate',
              transform: 'translateZ(0)'
            }
          }, [
            h('div', { class: 'tooltip-arrow' }),
            h('div', { class: 'tooltip-inner' }, this.text)
          ])
        ])
      )
    }

    return h('span', { style: { position: 'relative', display: 'inline-block' } }, children)
  }
}
